import { AxiosResponse } from 'axios';

import {
  Project,
  ProjectsAvailabilityByProjectKey,
} from '@qality/common-model';
import {
  StorageOfUniqueServices,
  AxiosWrapperService as axios,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

const SETTINGS_API_PATH = '/qalitySettings';
const UPDATE_BULK_QALITY_SETTINGS_URL = '/qalitySettings/bulk';

export default {
  async getSettingsForProject(project: { key: string }): Promise<Project> {
    try {
      const settings = await this.getProjectsAvailabilityByProjectKey();
      return appendSettingsToProject(settings, project);
    } catch (e) {
      return null;
    }
  },

  async getProjectsAvailabilityByProjectKey(): Promise<ProjectsAvailabilityByProjectKey> {
    const request =
      StorageOfUniqueServices.get('WindowOriginService').origin +
      SETTINGS_API_PATH;
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(request, 'projects=ALL');
    return (await axios.get(url)).data;
  },

  async getSettings(): Promise<Array<Project>> {
    try {
      const [allAdministeredProjects, settings] = await Promise.all([
        StorageOfUniqueServices.get(
          'AdministeredProjectService'
        ).getAllAdministeredProjects(),
        fetchSettingsForAdmin(),
      ]);

      return allAdministeredProjects.map((project) => {
        return appendSettingsToProject(settings, project);
      });
    } catch (e) {
      return null;
    }
  },

  async updateAppVisibilityInAllProjects(
    projects: Array<Project>,
    isAppVisibleInAllIssues: boolean
  ): Promise<AxiosResponse> {
    const settings = projects.map((project) => {
      return {
        ...project.getRequestDataToUpdateSettings(),
        isQalityEnabled: isAppVisibleInAllIssues,
      };
    });

    const request =
      StorageOfUniqueServices.get('WindowOriginService').origin +
      UPDATE_BULK_QALITY_SETTINGS_URL;
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(request);

    return axios.put(url, settings, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  async updatePluginEnabledInAllProjects(
    projects: Array<Project>,
    isPluginEnabled: boolean
  ): Promise<AxiosResponse> {
    const settings = projects.map((project) => {
      return {
        ...project.getRequestDataToUpdateSettings(),
        isPluginEnabled: isPluginEnabled,
      };
    });

    const request =
      StorageOfUniqueServices.get('WindowOriginService').origin +
      UPDATE_BULK_QALITY_SETTINGS_URL;
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(request);

    return axios.put(url, settings, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  async updateQAlitySettings(
    projectWithSettings: Project
  ): Promise<AxiosResponse> {
    const request =
      StorageOfUniqueServices.get('WindowOriginService').origin +
      SETTINGS_API_PATH;
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(request);

    return axios.put(
      url,
      projectWithSettings.getRequestDataToUpdateSettings(),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  },
};

function appendSettingsToProject(
  settings: ProjectsAvailabilityByProjectKey,
  project: {
    key: string;
  }
): Project {
  const isAppVisibleInAllIssues = !Utils.isNilOrEmpty(settings[project.key])
    ? settings[project.key].isQalityEnabled
    : true;
  const isPluginEnabled = !Utils.isNilOrEmpty(settings[project.key])
    ? settings[project.key].isPluginEnabled
    : true;
  return new Project({
    ...project,
    isAppVisibleInAllIssues,
    isPluginEnabled,
  });
}

async function fetchSettingsForAdmin(): Promise<ProjectsAvailabilityByProjectKey> {
  const request =
    StorageOfUniqueServices.get('WindowOriginService').origin +
    SETTINGS_API_PATH;

  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(request);

  return (await axios.get(url)).data;
}
